// Initial wizard API (api/Wizard/*) and first-login password reset
import { apiService } from './api';
import type {
  ConfigurationInitialRequest,
  DescriptionCompany,
  WizardDepartment,
} from '../types/wizard';

class WizardService
{
  async getDescriptionCompanies(): Promise<DescriptionCompany[]>
  {
    const response = await apiService.get<DescriptionCompany[]>('Wizard/GetDescriptionCompanies');
    return Array.isArray(response) ? response : [];
  }

  async getDepartmentsByDescriptionCompanyId(
    descriptionCompanyId: number,
  ): Promise<WizardDepartment[]>
  {
    const response = await apiService.get<WizardDepartment[]>(
      'Wizard/GetDepartmentsByDescriptionCompanyId',
      { params: { descriptionCompanyId } },
    );
    return Array.isArray(response) ? response : [];
  }

  /** Returns the (possibly new) DescriptionCompanyId */
  async addUpdateDescriptionCompanies(companies: DescriptionCompany[]): Promise<number>
  {
    const response = await apiService.post<number | string>(
      'Wizard/AddUpdateDescriptionCompanies',
      companies,
    );
    const parsed = Number(response);
    return isNaN(parsed) ? 0 : parsed;
  }

  async addUpdateDepartments(departments: WizardDepartment[]): Promise<WizardDepartment[]>
  {
    const response = await apiService.post<WizardDepartment[]>(
      'Wizard/AddUpdateDepartments',
      departments,
    );
    return Array.isArray(response) ? response : [];
  }

  async createConfigurationInitial(request: ConfigurationInitialRequest): Promise<void>
  {
    await apiService.post('Wizard/CreateConfigurationInitial', request);
  }

  /**
   * Resets the password with the token issued on the first login.
   * Returns false when the API reports the token as invalid.
   */
  async resetPassword(
    userId: string,
    token: string,
    newPassword: string,
    tenantString: string,
  ): Promise<boolean>
  {
    const params = new URLSearchParams({ userId, token, newPassword, tenantString });
    const response = await apiService.post<{ succeeded?: boolean; Succeeded?: boolean }>(
      `account/ResetPassword?${params.toString()}`,
    );
    const succeeded = response?.succeeded ?? response?.Succeeded;
    return succeeded !== false;
  }
}

export const wizardService = new WizardService();
export default wizardService;
